'use client';
import { useEffect, useRef } from 'react';
import { elevarBaseData } from '@/lib/elevar/cart-events';
import { elevarEnsureContext } from '@/lib/elevar/ensure-context';
import { useCart } from '@/contexts/CartContext';
import { elevarViewItemList } from '@/lib/elevar/item-view-list';
import { elevarPageTypePage } from '@/lib/elevar/page-type';
import { usePricing } from '@/contexts/PricingContext';

/**
 * Fires collection page events in order: `dl_user_data`, `dl_view_item_list`,
 * then the legacy `pageType: 'page'` push (legacy collections are Shopify pages).
 * `slug` builds list paths and referring collection context for downstream PDP/cart events.
 * Mount with `key={slug}` so the ref resets on navigation via remount.
 * Once `hasFiredRef` is marked, the sequence is committed to complete,
 * preventing dropped secondary events if dependencies update mid-flight.
 */
export default function ElevarCollection({ cardProducts, slug }) {
  const { cart } = useCart();
  const pricing = usePricing();
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (cart === null || hasFiredRef.current) return;

    let cancelled = false;

    (async () => {
      await elevarEnsureContext();
      if (cancelled) return;

      hasFiredRef.current = true;
      await elevarBaseData(cart, pricing);

      await elevarViewItemList(cardProducts, slug, pricing);
      elevarPageTypePage();
    })();

    return () => { cancelled = true; };
  }, [slug, cardProducts, cart]);

  return null;
}
